// Saga watcher factories for the commission service.
//
// Two things to know about this file.
//
// 1. `take` sits OUTSIDE the try block. The existing watchers in this codebase
//    put it inside:
//
//      while (true) { try { yield take(T); ... } catch (e) { ... } }
//
//    If anything throws while handling the error — and `unAuthorizedNotification`
//    can — the throw escapes the catch, the generator returns, and the watcher
//    is dead for the rest of the session. The next request then never resolves
//    and the UI spins forever. Taking outside the try makes that impossible.
//
// 2. redux-saga is pinned at 0.14.1, which has no `debounce`, no `takeLeading`
//    and no `delay` *effect* (only a promise helper on the root export). The
//    equivalents are built from take/fork/cancel/race below. See
//    docs/commissions-rewrite-plan.md.
//
// ES5 only: no optional chaining.

import {
  take,
  call,
  put,
  fork,
  cancel,
  race,
  actionChannel
} from 'redux-saga/effects';
import { delay, buffers } from 'redux-saga';
import { unAuthorizedNotification } from '../../lib/access.es6';
import { GAloggerException } from '../../lib/monitoring.es6';

var GENERIC_MESSAGE =
  'An error occurred, this could be due to invalid data';

// Turns an axios error into a consistent { message, statusCode, fields } shape.
// Callers get one thing to render regardless of how the service failed.
//
// This must NEVER throw — it runs inside the failure path of every watcher.
export function normalizeError(e) {
  var normalized = { message: GENERIC_MESSAGE, statusCode: null, raw: null };

  try {
    if (!e) return normalized;

    if (!e.response) {
      // No response at all: network failure, CORS, or a client-side throw.
      normalized.message = e.message || GENERIC_MESSAGE;
      return normalized;
    }

    var response = e.response;
    var data = response.data;

    normalized.statusCode = response.status || null;
    normalized.raw = data || null;

    if (data) {
      // NestJS validation errors put an array of strings on `message`.
      if (Array.isArray(data.message)) {
        normalized.message = data.message.join('. ');
      } else if (typeof data.message === 'string' && data.message) {
        normalized.message = data.message;
      } else if (typeof data.error === 'string' && data.error) {
        normalized.message = data.error;
      } else if (typeof data === 'string') {
        normalized.message = data;
      }
    }

    if (response.status === 409 && normalized.message === GENERIC_MESSAGE) {
      normalized.message = 'That record already exists.';
    }
  } catch (ignored) {
    // Deliberately swallowed: a failure to describe a failure must not
    // propagate and kill the watcher.
  }

  return normalized;
}

// Reports the error through the app's existing mechanisms. Isolated in a
// try/catch for the same reason as above.
function reportError(e) {
  try {
    if (e && e.response) unAuthorizedNotification(e);
    GAloggerException(e);
  } catch (ignored) {
    // Never let telemetry kill a watcher.
  }
}

// Resolves the arguments to pass to the request function. Request functions in
// requests.es6.js have mixed signatures — some take (params), some take
// (guid, payload) — so a watcher can supply a mapper instead of every call site
// reshaping its payload.
function argsFor(action, options) {
  if (options && typeof options.args === 'function') {
    var mapped = options.args(action.payload, action);
    return Array.isArray(mapped) ? mapped : [mapped];
  }
  return [action.payload];
}

// Runs one request-response cycle. Shared by every watcher below so the
// error handling exists exactly once.
export function* runRequest(apiFn, action, onSuccess, onFailure, options) {
  var opts = options || {};
  try {
    var args = argsFor(action, opts);
    var response = yield call.apply(null, [apiFn].concat(args));
    var data = response ? response.data : null;

    // Called with the request payload, as the success action creator is.
    if (typeof opts.onSuccessEffects === 'function') {
      yield* opts.onSuccessEffects(data, action.payload);
    }
    if (onSuccess) yield put(onSuccess(data, action.payload));
    return data;
  } catch (e) {
    reportError(e);
    if (onFailure) yield put(onFailure(normalizeError(e), action.payload));
    return null;
  }
}

// Standard watcher: handles every request, one at a time, forever.
// Use for independent writes and per-record lookups.
//
// Taken from a buffered channel, not a bare `take`: a bare take only sees
// actions dispatched while the loop is waiting, so anything dispatched during
// the request in flight is lost. The batch builder dispatches one bank lookup
// per selected agent in a single tick, and only the first ever ran.
export function makeWatcher(actionType, apiFn, onSuccess, onFailure, options) {
  return function* watcher() {
    var channel = yield actionChannel(actionType, buffers.expanding());
    for (;;) {
      // Outside the try — see note 1 at the top of this file.
      var action = yield take(channel);
      yield* runRequest(apiFn, action, onSuccess, onFailure, options);
    }
  };
}

// takeLatest equivalent: a new request cancels the one in flight, so stale
// responses can never arrive out of order. Use for list fetches, filter
// changes and the user-search typeahead.
export function makeLatestWatcher(
  actionType, apiFn, onSuccess, onFailure, options
) {
  return function* latestWatcher() {
    var task = null;
    for (;;) {
      var action = yield take(actionType);
      if (task) yield cancel(task);
      task = yield fork(runRequest, apiFn, action, onSuccess, onFailure, options);
    }
  };
}

// debounce equivalent: waits for `ms` of quiet before firing, and any new
// action during the wait replaces the pending one. Use for formula validation
// as a convenience alongside an explicit "Test formulas" button.
export function makeDebouncedWatcher(
  actionType, ms, apiFn, onSuccess, onFailure, options
) {
  return function* debouncedWatcher() {
    for (;;) {
      var action = yield take(actionType);

      // Keep resetting the timer while actions keep arriving.
      for (;;) {
        var outcome = yield race({
          next: take(actionType),
          quiet: call(delay, ms)
        });
        if (!outcome.next) break;
        action = outcome.next;
      }

      yield fork(runRequest, apiFn, action, onSuccess, onFailure, options);
    }
  };
}

// takeLeading equivalent: while a request is in flight, further actions of the
// same type are dropped. Use for anything that moves money, together with the
// reducer's `submitting` flag so the button is disabled too. The blocking
// `yield*` is what provides the guard — actions dispatched during it are never
// taken.
export function makeLeadingWatcher(
  actionType, apiFn, onSuccess, onFailure, options
) {
  return function* leadingWatcher() {
    for (;;) {
      var action = yield take(actionType);
      yield* runRequest(apiFn, action, onSuccess, onFailure, options);
    }
  };
}

// Cancellable poller. 0.14 has no `delay` effect, hence `call(delay, ms)`.
//
// `isDone(data)` ends the poll on a terminal state; `stopType` ends it on an
// action (unmount, or the user navigating away). Runs the first poll after one
// interval, on the assumption the caller already has fresh data.
export function makePoller(config) {
  var apiFn = config.apiFn;
  var intervalMs = config.intervalMs || 5000;
  var maxPolls = config.maxPolls || 120;

  function* pollLoop(action) {
    for (var count = 0; count < maxPolls; count += 1) {
      yield call(delay, intervalMs);
      var data = yield* runRequest(
        apiFn, action, config.onSuccess, config.onFailure, config
      );
      if (data && config.isDone && config.isDone(data)) return;
    }
  }

  return function* poller() {
    for (;;) {
      var action = yield take(config.startType);
      yield race({
        poll: call(pollLoop, action),
        stopped: take(config.stopType)
      });

      // Polling has ended — either it reached a terminal state, ran out of
      // attempts, or was stopped. Announce it either way so the "polling" flag
      // in the store cannot be left stuck on after the loop finishes by itself.
      if (config.onStopped) yield put(config.onStopped());
    }
  };
}
